import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import RadioGroup from "@material-ui/core/RadioGroup";
import TextFiled, { TextFieldProps } from "@material-ui/core/TextField";
import moment, { Moment } from "moment";
import React, { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { CompanySelect } from "../../../common/customAutocomplete/CompanySelect";
import { CountrySelect } from "../../../common/customAutocomplete/CountrySelect";
import { GenderSelect } from "../../../common/customAutocomplete/GenderSelect";
import { IPlaceOfOriginOption, PlaceOfOriginSelect } from "../../../common/customAutocomplete/PlaceOfOriginSelect";
import DatePicker from "../../../common/datePicker/DatePicker";

type TextField = TextFieldProps & {
  name: string;
  withErrorMessage?: boolean;
};

type DateField = {
  name: string;
  label: string;
  defaultValue?: Date | Moment | null;
  disabled?: boolean;
};

type RadioField = {
  name: string;
  label: string;
  defaultValue?: string;
  required?: boolean;
};

interface ISelectFieldProps {
  name: string;
  defaultValue?: string;
  required?: boolean;
  label?: string;
  disabled?: boolean;
}

export const TextInput: FC<TextField> = ({ name, withErrorMessage, ...props }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  return (
    <TextFiled
      {...register(name, { required: true })}
      {...props}
      fullWidth
      error={!!errors[name]}
      helperText={withErrorMessage && errors?.[name]?.message}
    />
  );
};

export const PlaceOfOriginSelectField: FC<ISelectFieldProps> = ({ name, defaultValue, required, disabled }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => (
        <PlaceOfOriginSelect
          value={value}
          error={!!errors[name]}
          required={required}
          onChange={(value: IPlaceOfOriginOption | null) => {
            onChange(value?.value);
          }}
          onBlur={onBlur}
          disabled={disabled}
        />
      )}
    />
  );
};

export const GenderSelectField: FC<ISelectFieldProps> = ({ name, defaultValue, required, disabled }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => (
        <GenderSelect
          disabled={disabled}
          value={value}
          error={!!errors[name]}
          required={required}
          onChange={onChange}
          onBlur={onBlur}
        />
      )}
    />
  );
};

export const CountrySelectField: FC<ISelectFieldProps> = ({ name, label, defaultValue, required, disabled }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => (
        <CountrySelect
          value={value}
          label={label || ""}
          error={!!errors[name]}
          required={required}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
        />
      )}
    />
  );
};

export const CompanySelectField: FC<ISelectFieldProps> = ({ name, label, defaultValue, required }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => (
        <CompanySelect
          value={value}
          label={label || ""}
          error={!!errors[name]}
          required={required}
          onChange={onChange}
          onBlur={onBlur}
        />
      )}
    />
  );
};

export const DateInput: FC<DateField> = ({ name, label, defaultValue, disabled }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field: { onBlur, onChange, value } }) => (
        <DatePicker
          onBlur={onBlur}
          onChange={(value: any) => {
            return onChange(!!value ? moment(value).format("YYYY-MM-DDTHH:mm:ss[Z]") : value);
          }}
          value={value}
          label={label}
          required
          error={!!errors[name]}
          disabled={disabled}
        />
      )}
    />
  );
};

export const RadioInput: FC<RadioField> = ({ name, label, defaultValue, required, children }) => {
  const { control } = useFormContext();
  return (
    <FormControl component="fieldset" required={required}>
      <FormLabel component="legend">{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => <RadioGroup {...field}>{children}</RadioGroup>}
      />
    </FormControl>
  );
};
